import { JobGrade, JobSpeciality } from '@/app/types/jobs';
import {
  DefaultRegisterForm,
  PreferredWorkLocation,
  RegistrationInformation,
} from '@/app/types/registration';
import { getRegisterCopy } from '@/app/utils/getRegisterCopy';
import {
  JobSearchGradeCookie,
  JobSearchSpecialityCookie,
  getGradeCookie,
  getLocationCookie,
  getSpecialityCookie,
  mapJobSearchLocationCookieToPreferredWorkLocation,
} from '@/app/utils/jobSearchCookies';
import { useAnalytics } from '@/app/utils/useAnalytics';
import { submitRegister } from '@/app/utils/useRequest';
import { LoginFormInitData } from '@/app/utils/userStateData';
import { Layout } from '@/modules/Layout';
import RegisterBasicInfo from '@/modules/RegistrationFlow/steps-old/RegisterBasicInfo';
import { whitepapers } from '@/modules/Whitepapers/whitepapers';
import { format } from 'date-fns';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

import type { RegisterFormProps, RegisterStage, RegistrationFlowProps } from './types';

const RegisterSimple = dynamic(() => import('@/modules/RegistrationFlow/steps-old/RegisterSimple'));

const RegisterVerifyEmail = dynamic(
  () => import('@/modules/RegistrationFlow/steps-old/RegisterVerifyEmail'),
);

const RegistrationFlowOld = ({
  previousRoute,
  preAction,
  referralCode,
  metadata,
  whitepaperSlug,
}: RegistrationFlowProps) => {
  const track = useAnalytics();
  const [registerError, setRegisterError] = useState<string>();
  const [stage, setStage] = useState<Array<RegisterStage>>(['basicInfo']);
  const [formData, setFormData] = useState<RegisterFormProps>(DefaultRegisterForm);
  const [canRegister, setCanRegister] = useState<boolean>(false);

  const jobSearchGrade = useRef<JobSearchGradeCookie>(getGradeCookie());
  const jobSearchSpecialty = useRef<JobSearchSpecialityCookie>(getSpecialityCookie());
  const jobSearchPreferredWorkLocation = useRef<PreferredWorkLocation>(
    mapJobSearchLocationCookieToPreferredWorkLocation(getLocationCookie()),
  );

  const isOnboardingQuestionsPrefilled =
    !!jobSearchGrade.current ||
    !!jobSearchSpecialty.current ||
    !!jobSearchPreferredWorkLocation.current;

  const trackingParams = {
    onboarding_questions_prefilled: isOnboardingQuestionsPrefilled,
    grade_prefilled: !!jobSearchGrade.current,
    speciality_prefilled: !!jobSearchSpecialty.current,
    preferred_workLocation_prefilled: !!jobSearchPreferredWorkLocation.current,
  };

  const registerCopy = getRegisterCopy(preAction, whitepaperSlug);

  const currentStage = stage[stage.length - 1];

  const onNext = (nextStage: RegisterStage) =>
    setStage(prevStage => {
      prevStage.push(nextStage);
      return [...prevStage];
    });

  const onGoback = () =>
    setStage(prevStage => {
      prevStage.pop();
      const sortedStage: RegisterStage[] = prevStage.length > 1 ? prevStage : ['basicInfo'];
      return [...sortedStage];
    });

  useEffect(() => {
    track('user registration started', { whitepaperName: whitepaperSlug });
  }, []);

  const getJobSearchFormData = (): Partial<RegisterFormProps> => {
    const jobSearchFormData: Partial<RegisterFormProps> = {};

    if (jobSearchGrade.current && jobSearchGrade.current !== 'none' && !formData?.grade) {
      jobSearchFormData.grade = jobSearchGrade.current;
    }

    if (
      jobSearchSpecialty.current &&
      jobSearchSpecialty.current !== 'none' &&
      !formData?.speciality?.key
    ) {
      jobSearchFormData.speciality = {
        key: jobSearchSpecialty.current,
        value: jobSearchSpecialty.current,
      };
    }

    if (jobSearchPreferredWorkLocation.current && !formData?.preferredWorkLocation?.key) {
      jobSearchFormData.preferredWorkLocation = jobSearchPreferredWorkLocation.current;
    }

    return jobSearchFormData;
  };

  useEffect(() => {
    const jobSearchFormData = getJobSearchFormData();
    const localStorageFormData = localStorage.getItem(LoginFormInitData);

    if (localStorageFormData) {
      const { firstName, lastName, email, area, phone } = JSON.parse(localStorageFormData);
      setFormData({
        ...formData,
        ...jobSearchFormData,
        firstName,
        lastName,
        email,
        phoneArea: area,
        phone,
      });
      localStorage.removeItem(LoginFormInitData);
    } else {
      setFormData({
        ...formData,
        ...jobSearchFormData,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const registerPost = async () => {
      const sortData: RegistrationInformation = {
        ...formData,
        grade: formData?.grade as JobGrade,
        speciality: formData?.speciality?.key as JobSpeciality,
        preferredWorkLocation: formData?.preferredWorkLocation?.key,
        preferredAvailability: formData?.preferredAvailability?.key,
        preferredWorkType: formData?.preferredWorkType?.key,
        graduationDate: formData?.graduationDate && format(formData?.graduationDate, 'yyyy-MM-01'),
        countryOfPrimaryDegree: formData?.countryOfPrimaryDegree?.key,
        countriesWorkedIn: formData?.countriesWorkedIn?.map(countryCodePair => countryCodePair.key),
        countryOfTraining: formData?.countryOfTraining?.key,
        phone: formData?.phone,
        redirectUrl: previousRoute ?? undefined,
        referralCode: referralCode ?? undefined,
      };
      if (whitepaperSlug) {
        sortData.whitepaperName = whitepapers.get(whitepaperSlug)?.name;
        // Don't redirect back to the whitepaper page after account verification, the user has already been sent the paper
        sortData.redirectUrl = undefined;
      }
      const result = await submitRegister(sortData);
      if ('error' in result) {
        const errMessage = result.error.message || 'Register Error. Please try again.';
        track('user registration failed', { details: result.error });
        setRegisterError(errMessage);
      } else {
        track('user registration completed', {
          whitepaperName: whitepaperSlug,
        });
        result.response.data.userId && onNext('verifyEmail');
      }
    };
    if (canRegister) {
      registerPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRegister]);

  const initState = () => {
    setStage(['basicInfo']);
    setCanRegister(false);
  };

  if (formData === undefined) {
    return null;
  }

  return (
    <Layout onClickRegisterFn={initState}>
      <NextSeo
        canonical={'/register'}
        title={metadata.title}
        description={metadata.description}
        openGraph={{
          title: metadata.title,
          description: metadata.description,
        }}
      />

      {currentStage === 'basicInfo' && (
        <RegisterBasicInfo
          whitepaperSlug={whitepaperSlug}
          formData={formData && formData}
          onContinue={data => {
            track('user registration basic info completed', {
              whitepaperName: whitepaperSlug,
            });
            setFormData({
              ...formData,
              ...data,
            });
            onNext('simple');
          }}
          preAction={preAction}
          previousRoute={previousRoute}
        />
      )}
      {currentStage === 'simple' && (
        <RegisterSimple
          title={registerCopy['simple']}
          formData={formData}
          onContinue={data => {
            track('user registration simple flow completed', {
              whitepaperName: whitepaperSlug,
              grade: data.grade,
              speciality: data.speciality?.value ?? 'not specified',
              ...trackingParams,
            });
            setFormData({ ...formData, ...data });
            setCanRegister(true);
          }}
          registerError={registerError}
          trackingParams={trackingParams}
        />
      )}
      {currentStage === 'verifyEmail' && (
        <RegisterVerifyEmail
          email={formData && formData.email}
          verifyEmailCopy={registerCopy['verifyEmail']}
        />
      )}
    </Layout>
  );
};

export default RegistrationFlowOld;
